import { useStore } from '@nanostores/react';
import { AnimatePresence, motion } from 'framer-motion';
import { atom } from 'nanostores';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocalStorage } from '../../hooks/browser/use-local-storage';
import * as styles from './index.css';

const DEFAULT_SIDEBAR_WIDTH = 370;
const MIN_SIDEBAR_WIDTH = 370;
const MAX_SIDEBAR_WIDTH = 740;
const SIDEBAR_ANIMATION_DURATION = 150;

const targetId = 'sidebar-portal';
const activeId = atom<string | null>(null);
const closingId = atom<string | null>(null);

export function open(id: string) {
  activeId.set(id);
  closingId.set(null);
}

export async function close() {
  closingId.set(activeId.get());
  activeId.set(null);
  await new Promise((resolve) => setTimeout(resolve, SIDEBAR_ANIMATION_DURATION));
  closingId.set(null);
}

export function toggle(id: string) {
  if (activeId.get() === id) {
    close();
  } else {
    open(id);
  }
}

// expose the sidebar functions to the window object
// so that the ember ui can hook into it and open/close the sidebar
// to prevent having two sidebars open at the same time
Object.assign(window, {
  __hydra_sidebar__: {
    open,
    close,
    subscribe: activeId.subscribe,
  } satisfies Window['__hydra_sidebar__'],
});

/**
 * Renders the sidebar root element.
 * This is the element that will be used as the target for the sidebar portal.
 */
export function SidebarRoot() {
  const _activeSidebarId = useStore(activeId);
  const isOpen = _activeSidebarId !== null;
  const [display, setDisplay] = useState<'block' | 'none'>('none');
  const [sidebarWidth, setSidebarWidth] = useLocalStorage('sidebar-width', DEFAULT_SIDEBAR_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || sidebarRef.current == null) return;

      const rect = sidebarRef.current.getBoundingClientRect();
      const newWidth = rect.right - e.clientX;
      const clampedWidth = Math.min(Math.max(newWidth, MIN_SIDEBAR_WIDTH), MAX_SIDEBAR_WIDTH);
      setSidebarWidth(clampedWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ew-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, setSidebarWidth]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={sidebarRef}
        animate={{
          width: isOpen ? sidebarWidth : 0,
          display,
        }}
        role="complementary"
        initial={{ width: 0 }}
        // putting 0 causes a slight scroll on the html element
        // when the sidebar is closed, so we use display: none instead
        // and set the width to 0
        onAnimationStart={() => {
          if (isOpen) {
            setDisplay('block');
          }
        }}
        onAnimationComplete={() => {
          if (!isOpen) {
            setDisplay('none');
          }
        }}
        exit={{
          display: 'none',
        }}
        transition={{
          duration: isResizing ? 0 : SIDEBAR_ANIMATION_DURATION / 1000,
          ease: 'easeInOut',
        }}
        style={{
          zIndex: 40, // pluma navbar z-index
          position: 'relative',
        }}
        className={styles.sidebarRoot}
        id={targetId}
      >
        {isOpen ? <div className={styles.resizeHandle} onMouseDown={handleMouseDown} /> : null}
      </motion.div>
    </AnimatePresence>
  );
}

export function Sidebar({
  id,
  children,
  onOpenChange,
}: {
  id: string;
  children: React.ReactNode;
  onOpenChange?: (isOpen: boolean) => void;
}) {
  const _activeSidebarId = useStore(activeId);
  const _closingSidebarId = useStore(closingId);
  const sidebarTarget = document.getElementById(targetId);
  const lastActiveId = useRef<string | null>(null);

  useEffect(() => {
    const wasActive = lastActiveId.current === id;
    const isActive = _activeSidebarId === id;

    if (lastActiveId.current !== _activeSidebarId) {
      lastActiveId.current = _activeSidebarId;

      if (wasActive && !isActive) {
        onOpenChange?.(false);
      }

      if (!wasActive && isActive) {
        onOpenChange?.(true);
      }
    }
  }, [_activeSidebarId, id, onOpenChange]);

  if (_activeSidebarId !== id && _closingSidebarId !== id) {
    return null;
  }

  if (sidebarTarget == null) {
    return null;
  }

  return <>{createPortal(<div className={styles.sidebar}>{children}</div>, sidebarTarget)}</>;
}

export function useSidebarActive(id: string) {
  const active = useStore(activeId) === id;
  const closing = useStore(closingId) === id;

  return active || closing;
}
